import React from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { ConfigProvider, theme } from 'antd';
import { ThemeProvider, useTheme } from '@/contexts/ThemeContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import Dashboard from '@/pages/Dashboard';
import Users from '@/pages/Users';
import Articles from '@/pages/Articles';
import Writers from '@/pages/Writers';
import Categories from '@/pages/Categories';
import Settings from '@/pages/Settings';
import Banners from '@/pages/Banners';
import Login from '@/pages/Login';
import { isAdminLoggedIn } from '@/services/auth';
import '@/App.css';

const RequireAuth = () => {
  const location = useLocation();

  if (!isAdminLoggedIn()) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <Outlet />;
};

const PublicOnly = () => {
  if (isAdminLoggedIn()) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
};

const ProtectedLayout = () => (
  <DashboardLayout>
    <Outlet />
  </DashboardLayout>
);

const FallbackRoute = () => (
  <Navigate to={isAdminLoggedIn() ? '/' : '/login'} replace />
);

const AppContent = () => {
  const { isDark } = useTheme();

  const antTheme = {
    algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: {
      fontFamily: 'Inter, sans-serif',
      borderRadius: 0,
      borderRadiusSM: 0,
      borderRadiusLG: 0,
      wireframe: true,
      colorPrimary: '#18181B',
      colorInfo: '#18181B',
      colorLink: '#18181B',
      paddingContentHorizontal: 24,
      controlHeight: 40,
    },
    components: {
      Button: {
        borderRadius: 0,
        controlHeight: 40,
        fontWeight: 500,
        defaultShadow: 'none',
        primaryShadow: 'none',
      },
      Card: {
        borderRadius: 0,
        boxShadow: 'none',
        actionsLiMargin: '0',
      },
      Input: {
        borderRadius: 0,
        controlHeight: 40,
        activeShadow: '0 0 0 2px rgba(24, 24, 27, 0.1)',
      },
      Table: {
        borderRadius: 0,
        headerBg: 'transparent',
        headerSplitColor: 'transparent',
        headerSortActiveBg: 'rgba(0,0,0,0.02)',
      },
      Modal: {
        borderRadius: 0,
      },
      Tag: {
        borderRadius: 0,
      },
      Select: {
        borderRadius: 0,
      },
    },
  };

  return (
    <ConfigProvider theme={antTheme}>
      <BrowserRouter>
        <Routes>
          <Route element={<PublicOnly />}>
            <Route path="/login" element={<Login />} />
          </Route>

          <Route element={<RequireAuth />}>
            <Route element={<ProtectedLayout />}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/users" element={<Users />} />
              <Route path="/articles" element={<Articles />} />
              <Route path="/writers" element={<Writers />} />
              <Route path="/categories" element={<Categories />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/banners" element={<Banners />} />
              <Route path="/ads" element={<Banners />} />
            </Route>
          </Route>

          <Route path="*" element={<FallbackRoute />} />
        </Routes>
      </BrowserRouter>
    </ConfigProvider>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}

export default App;
